import { Actions, CallPut } from '../enums/OrderTicket.enum'
import { orderLegSideEnum, orderOpenCloseEnum } from '../constants/order'

const equityActionsBySide = Object.freeze({
  BUY: Actions.Buy,
  SELL: Actions.Sell,
  SELL_SHORT: Actions.SellShort,
  BUY_TO_COVER: Actions.BuyCover,
})

const getOptionLegAction = ({ side, open_close }) => {
  const isBuy = side === orderLegSideEnum.BUY || side === 'BUY_TO_COVER'
  const isOpen = open_close === orderOpenCloseEnum.OPEN

  if (isBuy) return isOpen ? Actions.BuyOpen : Actions.BuyClose
  return isOpen ? Actions.SellOpen : Actions.SellClose
}

/**
 * Maps a bot-provided group trade ticket draft (the `draft_ticket` on a group
 * upload) onto the group trade ticket form state.
 *
 * Strategies the ticket can't select (e.g. COVERED_CALL, which the backend
 * derives from a CUSTOM option + equity order) fall back to CUSTOM, and a lone
 * equity leg is always STOCK.
 *
 * @param {object} draft - The draft ticket, shaped like the group order payload.
 * @param {Array<string>} supportedStrategies - Strategy values the ticket offers.
 * @returns {{
 *   symbol: string,
 *   strategy: string,
 *   action: number | null,
 *   equityAction: number | null,
 *   optionLegs: Array<{action: number, expiration: string, strike: string, optionRoot: string | null, type: number}>,
 *   orderType: string,
 *   limitPrice: string | undefined,
 *   stopPrice: string | undefined,
 *   duration: string,
 *   blockTradeAccount: string,
 *   debitCredit: boolean,
 * }}
 */
export const mapGroupTicketDraftToForm = (draft, supportedStrategies) => {
  const legs = draft.user_order_legs ?? []
  const equityLeg = legs.find((leg) => leg.leg_type === 'EQUITY')
  const equityAction = equityLeg ? equityActionsBySide[equityLeg.side] : null

  const optionLegs = legs
    .filter((leg) => leg.leg_type !== 'EQUITY')
    .map((leg) => ({
      action: getOptionLegAction(leg),
      expiration: leg.expiration,
      strike: leg.strike,
      optionRoot: leg.option_root ?? null,
      type: leg.leg_type === 'CALL' ? CallPut.call : CallPut.put,
    }))

  let strategy = 'CUSTOM'
  if (!optionLegs.length) strategy = 'STOCK'
  else if (supportedStrategies.includes(draft.strategy))
    strategy = draft.strategy

  const isStock = strategy === 'STOCK'

  return {
    symbol: draft.symbol,
    strategy,
    action: isStock ? equityAction : null,
    equityAction: isStock ? null : equityAction,
    optionLegs,
    orderType: draft.order_type,
    limitPrice: draft.limit_price ?? undefined,
    stopPrice: draft.stop_price ?? undefined,
    duration: draft.duration,
    blockTradeAccount: draft.block_trade_account,
    debitCredit: draft.side_override === orderLegSideEnum.BUY,
  }
}
